import { useEffect, useContext, useState, SetStateAction } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'
import uniqueWith from 'lodash/uniqWith'
import Icon from '@mdi/react'
import { mdiCircle } from '@mdi/js'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import { dealerMonitorEquipment } from '@racepointenergy/swc/scs/dealer-monitor-equipment'
import {
  EquipmentResultWithStatusPathParts,
  equipmentEntityAdapter,
} from '@racepointenergy/swc/scs/features/host-equipment'
import type { HomeId } from '@racepointenergy/swc/scs/types'

import { HomeStateContext } from 'features/home/homeStateContext'
import { selectedHomeSelector } from 'features/home/selectors'
import { H1 } from 'components/Typography/Headings'
import { messages } from '../messages'
import { commonMessages } from 'app/i18n/messages'
import StatusCard from './StatusCard'
import {
  ConnectionWrapper,
  DetailsWrapper,
  ErrorText,
  StatusCardsWrapper,
  StatusWrapper,
} from './styles'
import theme from 'theme_'
import { useAppSelector } from 'app/hooks'
import {
  homeActiveConfigEnergySelector,
  homeSoftwareVersionSelector,
} from 'features/home/selectors'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { SmartHardware } from 'features/home/types'
import CommonLinkButton from 'components/Button/CommonLinkButton'
import ROUTES from 'app/routes'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import SignalIcon from '../../HomeDevices/Device/DeviceSignalStrength/SignalIcon'
import HostIcon from 'components/HostIcon'
import DetailsModal from './DetailsModal'

const equipmentSelectors = equipmentEntityAdapter.getSelectors()
const equipmentInitialState = equipmentEntityAdapter.getInitialState()

const hostStateInitialState = stateEntityAdapter.getInitialState()

const statusClass = ['active', 'warning', 'inactive', 'inactive']
const statusColor = [
  theme.colors.successGreen,
  theme.colors.statusYellow,
  theme.colors.error,
  theme.colors.error,
]
const statusMessage = [
  messages.online,
  messages.warning,
  commonMessages.error,
  commonMessages.error,
]

interface SystemStatusProps {
  id: string
  homeId: HomeId
}

function SystemStatus({ id, homeId }: SystemStatusProps) {
  const { isOnline } = useContext(HomeStateContext)
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const currentHome = useAppSelector(selectedHomeSelector.data)
  const history = useHistory()
  const { formatMessage } = useIntl()

  const [offlineModules, setOfflineModules] = useState([] as SmartHardware[])
  const [hardwareLength, setHardwareLength] = useState(-1)
  const [circuitNames, setCircuitNames] = useState(
    {} as { [key: string]: string }
  )
  const [essName, setEssName] = useState('')
  const [connection, setConnection] = useState('')
  const [equipmentData, setEquipmentData] = useState(
    {} as EquipmentResultWithStatusPathParts | undefined
  )
  const [modalOpen, setModalOpen] = useState(false)

  const { softwareVersionLabel, createdDate } = useAppSelector(
    homeSoftwareVersionSelector
  )

  const { data: states = hostStateInitialState, isFetching: isStatesFetching } =
    dealerMonitorStates.useQuery({
      id: homeId,
    })
  const { data = equipmentInitialState } = dealerMonitorEquipment.useQuery({
    id: homeId,
  })

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const isCellular =
    currentHome?.uid?.substring(currentHome?.uid?.length - 4) === '0114' ||
    currentHome?.uid?.substring(currentHome?.uid?.length - 4) === '0116'

  useEffect(() => {
    if (essName) {
      const equipmentUpdate = equipmentSelectors.selectById(
        data,
        `System.Equipment.Components.${essName}`
      )
      setEquipmentData(equipmentUpdate)

      if (homeActiveConfigEnergy?.components?.length) {
        const conf = homeActiveConfigEnergy.components.find(
          (c) => c.name === essName
        )?.deviceID

        if (conf && homeActiveConfigEnergy?.connections?.length) {
          let conn = homeActiveConfigEnergy.connections.find(
            (c) => c.sourceDeviceID === conf
          )?.sourceConnector?.connectorName

          if (!conn) {
            conn = homeActiveConfigEnergy.connections.find(
              (c) => c.sinkDeviceID === conf
            )?.sinkConnector?.connectorName
          }

          if (conn) {
            setConnection(conn)
          }
        }
      }
    }
  }, [data, essName, homeActiveConfigEnergy])

  useEffect(() => {
    if (
      !isHomeActiveConfigEnergyLoaded ||
      !homeActiveConfigEnergy ||
      isStatesFetching ||
      !states?.entities
    ) {
      return
    }

    const smartHardware = homeActiveConfigEnergy.lightingDevices
      ?.filter((device) => device.smartHardware && device.type === 'PBC')
      .flatMap((device: { smartHardware: any }) => {
        return device.smartHardware
      })

    const areWithinOneDecimal = (num1: string, num2: string) => {
      let decimal1 = parseInt(num1, 16)
      let decimal2 = parseInt(num2, 16)

      // Only look up +1 from odd addresses
      const isOddToEven = decimal1 % 2 === 1 && decimal2 === decimal1 + 1
      const isEvenFromOdd = decimal2 % 2 === 1 && decimal1 === decimal2 + 1

      return (
        num1.substring(0, 8) === num2.substring(0, 8) &&
        (isOddToEven || isEvenFromOdd)
      )
    }

    const uniqueSmartHardware = uniqueWith(
      smartHardware,
      (arrVal: any, othVal: any) => {
        return (
          arrVal.hwUID === othVal.hwUID ||
          areWithinOneDecimal(arrVal.hwUID, othVal.hwUID)
        )
      }
    )

    setHardwareLength(uniqueSmartHardware?.length ?? 0)

    const om: SetStateAction<SmartHardware[]> = []

    uniqueSmartHardware?.forEach((hw) => {
      const circuitName = homeActiveConfigEnergy.energyConfig?.circuits?.find(
        (circuit: { companion: string | any[] }) =>
          circuit.companion?.includes(hw.hwUID)
      )?.name

      // Only process if we have a valid circuit name
      if (!circuitName) {
        return
      }

      const status =
        states.entities[`Energy.Circuit.${circuitName}.Status`]?.value

      // Only process if we have a valid status value (including 0)
      // If status is undefined, the states haven't loaded for this circuit yet
      if (status === undefined || status === null) {
        return
      }

      // Check if HasCommError bit (bit 6) is set
      if (status && (status & 0b1000000) !== 0) {
        om.push(hw)
      }
    })

    // Always update state to clear any stale offline modules when data is ready
    const cn: { [key: string]: string } = {}

    om.forEach((module: SmartHardware) => {
      const circuitName = homeActiveConfigEnergy.energyConfig?.circuits?.find(
        (circuit) => circuit.companion?.includes(module.hwUID)
      )?.name

      cn[module.hwUID] = circuitName ?? ''
    })

    setOfflineModules(om)
    setCircuitNames(cn)

    const essArray =
      homeActiveConfigEnergy.energyConfig?.controllers?.filter(
        (controller) => controller.microgridConfig
      ) ?? []
    setEssName(essArray.length ? essArray[0].name : '')
  }, [
    isHomeActiveConfigEnergyLoaded,
    homeActiveConfigEnergy,
    states.entities,
    isStatesFetching,
  ])

  const handleClick = () => {
    if (essName) {
      const devicePath = generatePath(ROUTES.homeDevice, {
        id: equipmentData?.StatusPath,
        homeId,
        rootDomainId: currentRootId ?? '',
      })
      return history.push(devicePath)
    }
  }

  return (
    <div>
      <H1>
        <FormattedMessage {...messages.systemStatusHeader} />
      </H1>
      <StatusCardsWrapper>
        <StatusCard
          loading={!currentHome}
          title={
            currentHome?.hostType === 'Linux-Director' ||
            currentHome?.hostType === 'Linux-Director-Lite' ||
            currentHome?.hostType === 'Linux-DirectorC'
              ? formatMessage(messages.director)
              : formatMessage(messages.savantSystem)
          }
          icon={<HostIcon home={currentHome || undefined} size={24} />}
          status={isOnline ? 'active' : 'inactive'}
        >
          {isCellular && (
            <ConnectionWrapper>
              {formatMessage(messages.cellular)}:
              <SignalIcon
                rsrp={
                  states.entities['global.NetworkConfig.cellular.rsrp']?.value
                }
                rsrq={
                  states.entities['global.NetworkConfig.cellular.rsrq']?.value
                }
                size={1}
              />
            </ConnectionWrapper>
          )}

          <ConnectionWrapper>
            {formatMessage(messages.wifi)}:
            <SignalIcon
              dbm={
                states.entities['global.NetworkConfig.wifi.signal_dBm']?.value
              }
              size={1}
            />
          </ConnectionWrapper>
        </StatusCard>

        <StatusCard
          loading={
            !isHomeActiveConfigEnergyLoaded ||
            !homeActiveConfigEnergy ||
            isStatesFetching ||
            !states?.entities
          }
          title={formatMessage(messages.powerModules)}
          icon={
            <Icon
              path="M 3.5 6 C 2.6715708 6 2 6.6715708 2 7.5 L 2 16.5 C 2 17.328399 2.6715708 18 3.5 18 L 20.5 18 C 21.328399 18 22 17.328399 22 16.5 L 22 7.5 C 22 6.6715708 21.328399 6 20.5 6 L 3.5 6 z M 3.5 7 L 20.5 7 C 20.7761 7 21 7.2238603 21 7.5 L 21 16.5 C 21 16.7761 20.7761 17 20.5 17 L 3.5 17 C 3.2238603 17 3 16.7761 3 16.5 L 3 7.5 C 3 7.2238603 3.2238603 7 3.5 7 z M 4.5 8.5 L 4.5 15.5 L 11.5 15.5 L 11.5 8.5 L 4.5 8.5 z M 13.5 8.5 L 13.5 9.5 L 14.5 9.5 L 14.5 8.5 L 13.5 8.5 z M 5.5 9.5 L 10.5 9.5 L 10.5 14.5 L 5.5 14.5 L 5.5 9.5 z M 13.5 10.5 L 13.5 15.5 L 14.5 15.5 L 14.5 10.5 L 13.5 10.5 z "
              size={1.5}
            />
          }
          status={offlineModules.length ? 'inactive' : 'active'}
        >
          <StatusWrapper>
            <Icon
              path={mdiCircle}
              size={0.5}
              color={theme.colors.successGreen}
            />
            <ErrorText color={theme.colors.duneWarmGrey}>
              {`${formatMessage(messages.connected)} ${
                hardwareLength - offlineModules.length
              }/${hardwareLength}`}
            </ErrorText>
          </StatusWrapper>

          {offlineModules.length > 0 && (
            <>
              <StatusWrapper>
                <Icon path={mdiCircle} size={0.5} color={theme.colors.error} />
                <ErrorText color={theme.colors.error}>
                  {offlineModules.length > 1
                    ? formatMessage(messages.modulesOffline, {
                        number: offlineModules.length,
                      })
                    : formatMessage(messages.moduleOffline, {
                        number: offlineModules.length,
                      })}
                </ErrorText>
              </StatusWrapper>

              <div>
                <strong>{`${offlineModules[0].hwUID} `}</strong>
                {offlineModules.length > 1 && (
                  <>
                    {formatMessage(messages.more, {
                      number: offlineModules.length - 1,
                    })}
                  </>
                )}
              </div>
              <DetailsWrapper>
                <CommonLinkButton onClick={() => setModalOpen(true)}>
                  <FormattedMessage {...messages.details} />
                </CommonLinkButton>
              </DetailsWrapper>
            </>
          )}
        </StatusCard>

        <StatusCard
          loading={!essName || !equipmentData}
          title={formatMessage(messages.ess)}
          icon={
            <Icon
              path="M 7.5 3 C 6.6715708 3 6 3.6715708 6 4.5 L 6 20 C 6 20.828399 6.6715708 21.5 7.5 21.5 L 8 21.5 L 8 22 L 9 22 L 9 21.5 L 15 21.5 L 15 22 L 16 22 L 16 21.5 L 16.5 21.5 C 17.328399 21.5 18 20.828399 18 20 L 18 4.5 C 18 3.6715708 17.328399 3 16.5 3 L 7.5 3 z M 7.5 4 L 16.5 4 C 16.7761 4 17 4.2238603 17 4.5 L 17 13 L 7 13 L 7 4.5 C 7 4.2238603 7.2238603 4 7.5 4 z M 15 7.5 L 15 10.5 L 16 10.5 L 16 7.5 L 15 7.5 z M 7 14 L 17 14 L 17 15.5 L 7 15.5 L 7 14 z M 7 16.5 L 17 16.5 L 17 18 L 7 18 L 7 16.5 z M 7 19 L 17 19 L 17 20 C 17 20.2761 16.7761 20.5 16.5 20.5 L 7.5 20.5 C 7.2238603 20.5 7 20.2761 7 20 L 7 19 z "
              size={1}
            />
          }
          status={statusClass[equipmentData?.Status ?? 3]}
        >
          <StatusWrapper>
            <Icon
              path={mdiCircle}
              size={0.5}
              color={statusColor[equipmentData?.Status ?? 3]}
            />
            {formatMessage(statusMessage[equipmentData?.Status ?? 3])}
          </StatusWrapper>
          <div>
            <div>
              <FormattedMessage {...messages.deviceModel} />
            </div>
            <strong>{equipmentData?.DeviceModel}</strong>
          </div>

          <div>
            <div>
              <FormattedMessage {...messages.communicationInterface} />
            </div>
            <strong>{connection ?? ''}</strong>
          </div>

          {equipmentData?.StatusString && (
            <div>{equipmentData?.StatusString}</div>
          )}

          <DetailsWrapper>
            <CommonLinkButton onClick={handleClick}>
              <FormattedMessage {...messages.details} />
            </CommonLinkButton>
          </DetailsWrapper>
        </StatusCard>

        <StatusCard
          title={formatMessage(messages.updates)}
          icon={
            <Icon
              path="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.04 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z"
              size={1}
            />
          }
          status="active"
        >
          <div>
            <div>
              <FormattedMessage {...messages.directorVersion} />
            </div>
            <strong>{softwareVersionLabel}</strong>
          </div>
          <div>
            <div>
              <FormattedMessage {...messages.updated} />
            </div>
            <strong>
              <IntlFormattedDate date={createdDate} withTime />
            </strong>
          </div>
        </StatusCard>
      </StatusCardsWrapper>

      <DetailsModal
        modalOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        offlineModules={offlineModules}
        circuitNames={circuitNames}
      />
    </div>
  )
}

export default SystemStatus
